import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { NormalizedComparisonPoint } from '../types';

interface ComparisonTimelineChartProps {
  data: NormalizedComparisonPoint[];
  activeMetric: 'presence' | 'messages' | 'clicks' | 'quiz' | 'surveys';
}

export const ComparisonTimelineChart = ({ data, activeMetric }: ComparisonTimelineChartProps) => {
  const { t } = useTranslation('analytics');
  const chartData = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.map((point) => ({
      ...point,
      displayOffset: t('comparator.minuteOffset', { minute: point.minuteOffset }),
    }));
  }, [data, t]);

  const dataKeyA = `${activeMetric}A`;
  const dataKeyB = `${activeMetric}B`;

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-[400px] bg-white border border-[#f3f3f4] rounded-[20px] flex items-center justify-center">
        <p className="text-role-tertiary font-medium italic">{t('comparator.empty.hint')}</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-white border border-[#f3f3f4] rounded-[20px] p-8 shadow-sm flex flex-col gap-6">
      <div className="h-[350px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorA" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2c6cff" stopOpacity={0.1} />
                <stop offset="95%" stopColor="#2c6cff" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorB" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff6526" stopOpacity={0.1} />
                <stop offset="95%" stopColor="#ff6526" stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />

            <XAxis
              dataKey="minuteOffset"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9ca3af', fontSize: 11 }}
              unit=" min"
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9ca3af', fontSize: 11 }}
              allowDecimals={false}
            />

            <Tooltip content={<CustomTooltip activeMetric={activeMetric} />} />

            <Area
              type="monotone"
              dataKey={dataKeyA}
              stroke="#2c6cff"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorA)"
              name={t('comparator.webinarA')}
              animationDuration={1000}
            />

            <Area
              type="monotone"
              dataKey={dataKeyB}
              stroke="#ff6526"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorB)"
              name={t('comparator.webinarB')}
              animationDuration={1000}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label, activeMetric }: any) => {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('analytics');
  if (active && payload && payload.length >= 2) {
    return (
      <div className="bg-dark text-white p-4 rounded-xl shadow-xl border border-white/10 text-xs min-w-[180px]">
        <p className="font-bold mb-3 border-b border-white/10 pb-2 text-sm">
          {tCommon('time.minuteOffset', { offset: label })}
        </p>
        <div className="space-y-2">
          <div className="flex justify-between items-center gap-4">
            <span className="opacity-60">{t('comparator.tooltip.webinarA')}</span>
            <span className="font-bold text-[#2c6cff] text-sm">
              {payload[0].value} {t(`comparator.tooltip.labelMap.${activeMetric}`)}
            </span>
          </div>
          <div className="flex justify-between items-center gap-4">
            <span className="opacity-60">{t('comparator.tooltip.webinarB')}</span>
            <span className="font-bold text-[#ff6526] text-sm">
              {payload[1].value} {t(`comparator.tooltip.labelMap.${activeMetric}`)}
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};
